/**
 * WEARING MIND - Animation Engine
 * Vanilla JS - IntersectionObserver based scroll animations
 */

class WearingMindAnimations {
  constructor() {
    this.init();
  }

  init() {
    this.setupScrollAnimations();
    this.setupTextSplitting();
    this.setupParallax();
  }

  // Scroll-triggered animations using IntersectionObserver
  setupScrollAnimations() {
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    
    if (!animatedElements.length) return;

    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          
          // Unobserve after animation to improve performance
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    animatedElements.forEach(el => observer.observe(el));
  }

  // Split text into characters for progressive animation
  setupTextSplitting() {
    const textElements = document.querySelectorAll('[data-text-split]');
    
    textElements.forEach(element => {
      const text = element.textContent;
      const lines = text.split('\n').filter(line => line.trim());
      
      element.innerHTML = '';
      
      lines.forEach((line, lineIndex) => {
        const lineWrapper = document.createElement('span');
        lineWrapper.className = 'text-split__line';
        
        const chars = line.split('');
        chars.forEach((char, charIndex) => {
          const charSpan = document.createElement('span');
          charSpan.className = 'text-split__char';
          charSpan.textContent = char === ' ' ? '\u00A0' : char;
          charSpan.style.animationDelay = `${(lineIndex * 100) + (charIndex * 30)}ms`;
          lineWrapper.appendChild(charSpan);
        });
        
        element.appendChild(lineWrapper);
      });
    });
  }

  // Smooth parallax scroll effect
  setupParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (!parallaxElements.length) return;

    let rafId = null;

    const updateParallax = () => {
      parallaxElements.forEach(element => {
        const speed = parseFloat(element.dataset.parallax) || 0.5;
        const rect = element.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const elementTop = rect.top + scrolled;
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight && rect.bottom > 0) {
          const yPos = (scrolled - elementTop) * speed;
          element.style.transform = `translate3d(0, ${yPos}px, 0)`;
        }
      });
      
      rafId = null;
    };

    const onScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(updateParallax);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    updateParallax();
  }
}

// Initialize animations when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new WearingMindAnimations();
  });
} else {
  new WearingMindAnimations();
}

/**
 * Stagger Animation Helper
 * Add staggered delays to child elements
 */
function staggerAnimation(container, delay = 100) {
  const children = container.querySelectorAll('.animate-on-scroll');
  children.forEach((child, index) => {
    child.setAttribute('data-delay', index + 1);
  });
}

// Export for use in sections
window.WearingMind = window.WearingMind || {};
window.WearingMind.staggerAnimation = staggerAnimation;
